import TextField from '@project/common/components/SettingsTextField';
import React from 'react';
import { useTranslation } from 'react-i18next';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import LabelWithHoverEffect from '@project/common/components/LabelWithHoverEffect';
import SwitchLabelWithHoverEffect from '@project/common/components/SwitchLabelWithHoverEffect';
import Radio from '@mui/material/Radio';
import { isWebmMediaFragmentSupported, PostMineAction, PostMinePlayback } from '@project/common';
import type { AsbplayerSettings } from '@project/common/settings';
import Switch from '@mui/material/Switch';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import SettingsSection from '@project/common/components/SettingsSection';
import NumericSettingInput from '@project/common/components/NumericSettingInput';

interface Props {
    settings: AsbplayerSettings;
    onSettingChanged: <K extends keyof AsbplayerSettings>(key: K, value: AsbplayerSettings[K]) => Promise<void>;
    showWebmMediaFragmentSettings?: boolean;
    supportsUpdateLastWithSameSubtitleText?: boolean;
}

const MiningSettingsTab: React.FC<Props> = ({
    settings,
    onSettingChanged,
    supportsUpdateLastWithSameSubtitleText,
    showWebmMediaFragmentSettings = true,
}) => {
    const { t } = useTranslation();
    const webmCaptureSupported = showWebmMediaFragmentSettings && isWebmMediaFragmentSupported();
    const {
        audioPaddingStart,
        audioPaddingEnd,
        maxImageWidth,
        maxImageHeight,
        mediaFragmentFormat,
        mediaFragmentTrimStart,
        mediaFragmentTrimEnd,
        mediaFragmentMaxClipLength,
        trimBlackBars,
        streamingScreenshotDelay,
        surroundingSubtitlesCountRadius,
        surroundingSubtitlesTimeRadius,
        clickToMineDefaultAction,
        postMiningPlaybackState,
        recordWithAudioPlayback,
        preferMp3,
        copyToClipboardOnMine,
        alwaysUseSubtitleForSentence,
        updateLastCardForSameSubtitle,
    } = settings;
    return (
        <Stack spacing={1}>
            <FormControl>
                <FormLabel component="legend">{t('settings.clickToMineDefaultAction')}</FormLabel>
                <RadioGroup row={false}>
                    <LabelWithHoverEffect
                        control={
                            <Radio
                                checked={clickToMineDefaultAction === PostMineAction.showAnkiDialog}
                                value={PostMineAction.showAnkiDialog}
                                onChange={(event) =>
                                    event.target.checked &&
                                    void onSettingChanged('clickToMineDefaultAction', PostMineAction.showAnkiDialog)
                                }
                            />
                        }
                        label={t('postMineAction.showAnkiDialog')}
                    />
                    <LabelWithHoverEffect
                        control={
                            <Radio
                                checked={clickToMineDefaultAction === PostMineAction.updateLastCard}
                                value={PostMineAction.updateLastCard}
                                onChange={(event) =>
                                    event.target.checked &&
                                    void onSettingChanged('clickToMineDefaultAction', PostMineAction.updateLastCard)
                                }
                            />
                        }
                        label={t('postMineAction.updateLastCard')}
                    />
                    <LabelWithHoverEffect
                        control={
                            <Radio
                                checked={clickToMineDefaultAction === PostMineAction.showUpdateCardDialog}
                                value={PostMineAction.showUpdateCardDialog}
                                onChange={(event) =>
                                    event.target.checked &&
                                    void onSettingChanged(
                                        'clickToMineDefaultAction',
                                        PostMineAction.showUpdateCardDialog
                                    )
                                }
                            />
                        }
                        label={t('postMineAction.showUpdateCardDialog')}
                    />
                    <LabelWithHoverEffect
                        control={
                            <Radio
                                checked={clickToMineDefaultAction === PostMineAction.exportCard}
                                value={PostMineAction.exportCard}
                                onChange={(event) =>
                                    event.target.checked &&
                                    void onSettingChanged('clickToMineDefaultAction', PostMineAction.exportCard)
                                }
                            />
                        }
                        label={t('postMineAction.exportCard')}
                    />
                    <LabelWithHoverEffect
                        control={
                            <Radio
                                checked={clickToMineDefaultAction === PostMineAction.none}
                                value={PostMineAction.none}
                                onChange={(event) =>
                                    event.target.checked &&
                                    void onSettingChanged('clickToMineDefaultAction', PostMineAction.none)
                                }
                            />
                        }
                        label={t('postMineAction.none')}
                    />
                </RadioGroup>
            </FormControl>

            <FormControl>
                <FormLabel component="legend">{t('settings.postMinePlayback')}</FormLabel>
                <RadioGroup row={false}>
                    <LabelWithHoverEffect
                        control={
                            <Radio
                                checked={postMiningPlaybackState === PostMinePlayback.remember}
                                value={PostMinePlayback.remember}
                                onChange={(event) =>
                                    event.target.checked &&
                                    void onSettingChanged('postMiningPlaybackState', PostMinePlayback.remember)
                                }
                            />
                        }
                        label={t('postMinePlayback.remember')}
                    />
                    <LabelWithHoverEffect
                        control={
                            <Radio
                                checked={postMiningPlaybackState === PostMinePlayback.play}
                                value={PostMinePlayback.play}
                                onChange={(event) =>
                                    event.target.checked &&
                                    void onSettingChanged('postMiningPlaybackState', PostMinePlayback.play)
                                }
                            />
                        }
                        label={t('postMinePlayback.play')}
                    />
                    <LabelWithHoverEffect
                        control={
                            <Radio
                                checked={postMiningPlaybackState === PostMinePlayback.pause}
                                value={PostMinePlayback.pause}
                                onChange={(event) =>
                                    event.target.checked &&
                                    void onSettingChanged('postMiningPlaybackState', PostMinePlayback.pause)
                                }
                            />
                        }
                        label={t('postMinePlayback.pause')}
                    />
                </RadioGroup>
            </FormControl>
            <SwitchLabelWithHoverEffect
                control={
                    <Switch
                        checked={copyToClipboardOnMine}
                        onChange={(event) => onSettingChanged('copyToClipboardOnMine', event.target.checked)}
                    />
                }
                label={t('settings.copyOnMine')}
                labelPlacement="start"
            />
            <SwitchLabelWithHoverEffect
                control={
                    <Switch
                        checked={alwaysUseSubtitleForSentence}
                        onChange={(event) => onSettingChanged('alwaysUseSubtitleForSentence', event.target.checked)}
                    />
                }
                label={t('settings.alwaysUseSubtitleForSentence')}
                labelPlacement="start"
            />
            {supportsUpdateLastWithSameSubtitleText && (
                <SwitchLabelWithHoverEffect
                    control={
                        <Switch
                            checked={updateLastCardForSameSubtitle}
                            onChange={(event) =>
                                onSettingChanged('updateLastCardForSameSubtitle', event.target.checked)
                            }
                        />
                    }
                    label={t('settings.updateLastCardForSameSubtitle')}
                    labelPlacement="start"
                />
            )}
            <SettingsSection>{t('settings.audio')}</SettingsSection>
            <SwitchLabelWithHoverEffect
                control={
                    <Switch
                        checked={recordWithAudioPlayback}
                        onChange={(event) => onSettingChanged('recordWithAudioPlayback', event.target.checked)}
                    />
                }
                label={t('settings.recordWithAudioPlayback')}
                labelPlacement="start"
            />
            <SwitchLabelWithHoverEffect
                control={
                    <Switch
                        checked={preferMp3}
                        onChange={(event) => onSettingChanged('preferMp3', event.target.checked)}
                    />
                }
                label={t('settings.mp3Preference')}
                labelPlacement="start"
            />

            <NumericSettingInput
                label={t('settings.audioPaddingStart')}
                fullWidth
                value={audioPaddingStart}
                color="primary"
                onValueChange={(value) => void onSettingChanged('audioPaddingStart', value)}
                slotProps={{
                    htmlInput: {
                        min: 0,
                        step: 1,
                    },
                    input: {
                        endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                    },
                }}
            />
            <NumericSettingInput
                label={t('settings.audioPaddingEnd')}
                fullWidth
                value={audioPaddingEnd}
                color="primary"
                onValueChange={(value) => void onSettingChanged('audioPaddingEnd', value)}
                slotProps={{
                    htmlInput: {
                        step: 1,
                        min: 0,
                    },
                    input: {
                        endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                    },
                }}
            />
            <SettingsSection>{t('settings.screenshots')}</SettingsSection>
            {showWebmMediaFragmentSettings && webmCaptureSupported && (
                <TextField
                    select
                    fullWidth
                    label={t('settings.mediaFragmentCaptureFormat')}
                    value={mediaFragmentFormat}
                    onChange={(event) =>
                        onSettingChanged(
                            'mediaFragmentFormat',
                            event.target.value as AsbplayerSettings['mediaFragmentFormat']
                        )
                    }
                >
                    <MenuItem value="jpeg">{t('settings.mediaFragmentFormatScreenshot')}</MenuItem>
                    <MenuItem value="webm">{t('settings.mediaFragmentFormatVideoClip')}</MenuItem>
                </TextField>
            )}
            <NumericSettingInput
                label={t('settings.maxImageWidth')}
                fullWidth
                value={maxImageWidth}
                color="primary"
                onValueChange={(value) => void onSettingChanged('maxImageWidth', value)}
                slotProps={{
                    htmlInput: {
                        min: 0,
                        step: 1,
                    },
                }}
            />
            <NumericSettingInput
                label={t('settings.maxImageHeight')}
                fullWidth
                value={maxImageHeight}
                color="primary"
                onValueChange={(value) => void onSettingChanged('maxImageHeight', value)}
                slotProps={{
                    htmlInput: {
                        min: 0,
                        step: 1,
                    },
                }}
            />
            <SwitchLabelWithHoverEffect
                control={
                    <Switch
                        checked={trimBlackBars}
                        onChange={(event) => onSettingChanged('trimBlackBars', event.target.checked)}
                    />
                }
                label={t('settings.trimBlackBars')}
                labelPlacement="start"
            />
            {showWebmMediaFragmentSettings && mediaFragmentFormat === 'webm' && webmCaptureSupported && (
                <>
                    <NumericSettingInput
                        label={t('settings.mediaFragmentTrimStart')}
                        fullWidth
                        value={mediaFragmentTrimStart}
                        color="primary"
                        onValueChange={(value) => void onSettingChanged('mediaFragmentTrimStart', value)}
                        slotProps={{
                            htmlInput: {
                                step: 100,
                            },
                            input: {
                                endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                            },
                        }}
                    />
                    <NumericSettingInput
                        label={t('settings.mediaFragmentTrimEnd')}
                        fullWidth
                        value={mediaFragmentTrimEnd}
                        color="primary"
                        onValueChange={(value) => void onSettingChanged('mediaFragmentTrimEnd', value)}
                        slotProps={{
                            htmlInput: {
                                step: 100,
                            },
                            input: {
                                endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                            },
                        }}
                    />
                    <NumericSettingInput
                        label={t('settings.mediaFragmentMaxClipLength')}
                        fullWidth
                        value={mediaFragmentMaxClipLength}
                        color="primary"
                        onValueChange={(value) => void onSettingChanged('mediaFragmentMaxClipLength', value)}
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                step: 1000,
                            },
                            input: {
                                endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                            },
                        }}
                    />
                </>
            )}
            {(!showWebmMediaFragmentSettings || mediaFragmentFormat === 'jpeg') && (
                <NumericSettingInput
                    label={t('extension.settings.screenshotCaptureDelay')}
                    fullWidth
                    value={streamingScreenshotDelay}
                    color="primary"
                    integerOnly
                    onValueChange={(value) => void onSettingChanged('streamingScreenshotDelay', value)}
                    slotProps={{
                        htmlInput: {
                            step: 100,
                        },
                        input: {
                            endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                        },
                    }}
                />
            )}
            <SettingsSection>{t('settings.exportDialog')}</SettingsSection>
            <NumericSettingInput
                label={t('settings.surroundingSubtitlesCountRadius')}
                fullWidth
                value={surroundingSubtitlesCountRadius}
                color="primary"
                onValueChange={(value) => void onSettingChanged('surroundingSubtitlesCountRadius', value)}
                slotProps={{
                    htmlInput: {
                        min: 1,
                        step: 1,
                    },
                }}
            />
            <NumericSettingInput
                label={t('settings.surroundingSubtitlesTimeRadius')}
                fullWidth
                value={surroundingSubtitlesTimeRadius}
                color="primary"
                onValueChange={(value) => void onSettingChanged('surroundingSubtitlesTimeRadius', value)}
                slotProps={{
                    htmlInput: {
                        min: 0,
                        step: 1,
                    },
                    input: {
                        endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                    },
                }}
            />
        </Stack>
    );
};

export default MiningSettingsTab;
